import React, { FunctionComponent, useRef } from 'react';
import { map, addIndex } from 'ramda';
import withStyles from 'react-jss';
import { Grid } from 'semantic-ui-react';
import { PieChart, Pie, Cell } from 'recharts';

import Legend from 'components/Legend';
import DownloadWidget from 'components/DownloadWidget';
import { ClassProp } from 'helpers/types';

import { PieLabelProps } from 'components/LapsedActivity/LapsedActivity.helpers';
import { RATIO_CHART_LEGEND } from 'scenes/NetworkHealth/sections/CardEventsSection/CardEventsSection.helpers';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  CNTransactionsRatio: any;
};

const styles = {
  grid: {
    margin: '1rem 0 0 0 !important',
  },
  radialChart: {
    display: 'inline-block !important',
    backgroundColor: '#fff !important',
    padding: '1.5rem 1rem !important',
    boxShadow:
      '0 .0625rem 0 0 rgba(0,0,0,0.03), 0 .125rem .375rem 0 rgba(0,0,0,0.06) !important',
    margin: '0 !important',
    justifyContent: 'center !important',
  },
  row: {
    padding: '0 !important',
  },
  centered: {
    textAlign: 'center !important',
    padding: '.75rem 0 !important',
  },
  contentRef: {
    padding: '0 !important',
  },
};

const mapIndexed = addIndex<any>(map);

const getPoint = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
}: PieLabelProps) => {
  const RADIAN = Math.PI / 180;
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return { x, y };
};

const renderCustomizedLabel = ({ cx, angle, index, ...rest }: any) => {
  const { x, y } = getPoint({ ...rest, cx });

  return (
    <text
      x={x}
      y={y}
      fill={'rgba(74,74,74, .8)'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {angle}
    </text>
  );
};

const renderCells = mapIndexed(({ color }: any, index: number) => (
  <Cell key={`cell-${index}`} fill={color} />
));

const EventRadialChart: FunctionComponent<Props> = ({
  classes,
  CNTransactionsRatio,
}) => {
  const contentRef = useRef(null);

  return (
    <Grid className={classes.grid}>
      <DownloadWidget
        nodeRef={contentRef}
        options={['PNG', 'PDF']}
        imageName="EventRadio"
      />

      <div className={classes.contentRef} ref={contentRef}>
        <Grid.Row className={classes.radialChart}>
          <Grid.Row className={classes.row}>
            <PieChart width={300} height={300}>
              <Pie
                data={CNTransactionsRatio}
                label={renderCustomizedLabel}
                labelLine
                isAnimationActive={false}
                nameKey="label"
                dataKey="angle"
                innerRadius={50}
                outerRadius={100}
                startAngle={90}
                endAngle={-270}
              >
                {renderCells(CNTransactionsRatio)}
              </Pie>
            </PieChart>
          </Grid.Row>

          <Grid.Row className={classes.centered}>
            <Legend legend={RATIO_CHART_LEGEND} />
          </Grid.Row>
        </Grid.Row>
      </div>
    </Grid>
  );
};
export default withStyles(styles)(EventRadialChart);
